/**
 * Linea del tempo orizzontale di un mercato (docs/TODO.md).
 *
 * ## Perché una barra e non una tabella
 *
 * Gli stessi dati stanno già altrove in forma tabellare — «Orario di borsa»
 * elenca le fasi, «Storico briefing» elenca i resoconti. Quello che nessuna
 * tabella può dare è la **distanza**: quanto manca al trigger, se
 * quell'operazione è avvenuta a mercato aperto o nel dopo-borsa, se il
 * resoconto è arrivato prima o dopo l'apertura. È un'informazione posizionale,
 * e si legge solo su un asse.
 *
 * ## La posizione è calcolata qui, la classificazione no
 *
 * Le percentuali sull'asse sono aritmetica di presentazione e stanno nel
 * componente. Ciò che invece **non** si decide qui è se un evento è futuro:
 * arriva dal server (`future`), perché dipende dallo stesso orologio che
 * definisce la finestra — e l'orologio del browser può essere spostato di ore
 * rispetto a quello che ha prodotto la barra.
 *
 * ## Sovrapposizioni
 *
 * Più eventi possono cadere nello stesso minuto (sei analisi che finiscono
 * insieme). Impilarli sulla stessa ascissa li renderebbe illeggibili, quindi
 * gli indicatori sull'asse sono **puntini** e il contenuto vive nell'elenco
 * sotto, cronologico e completo. La barra dice *quando*, l'elenco dice *cosa*:
 * nessuna delle due prova a fare entrambe le cose in poco spazio.
 */

import { Link } from 'react-router-dom';
import { Alert, Badge, Card, Icon, Spinner, type IconName } from '@/ui';
import { useMarketTimeline, type TimelineEvent, type TimelinePhase } from './api';
import './timeline.css';

const PHASE_LABELS: Record<TimelinePhase, string> = {
  pre_market: 'Pre-market',
  opening_auction: 'Asta di apertura',
  open: 'Sessione continua',
  lunch_break: 'Pausa pranzo',
  closing_auction: 'Asta di chiusura',
  after_hours: 'After-hours',
};

const EVENT_ICONS: Record<TimelineEvent['kind'], IconName> = {
  trigger: 'trigger',
  briefing: 'notification',
  analysis: 'ai',
  proposal: 'chart',
  order: 'transfer',
};

const EVENT_KIND_LABELS: Record<TimelineEvent['kind'], string> = {
  trigger: 'Innesco',
  briefing: 'Resoconto',
  analysis: 'Analisi',
  proposal: 'Proposta',
  order: 'Ordine',
};

const TONE_TO_BADGE: Record<TimelineEvent['tone'], 'neutral' | 'accent' | 'positive' | 'negative' | 'warning'> = {
  neutral: 'neutral',
  accent: 'accent',
  positive: 'positive',
  negative: 'negative',
  warning: 'warning',
};

/** Posizione percentuale di un istante sull'asse, limitata ai bordi della barra. */
function positionOf(instant: string, start: number, span: number): number {
  const value = ((new Date(instant).getTime() - start) / span) * 100;
  return Math.min(100, Math.max(0, value));
}

/** Ora locale **della borsa**, non del browser: la barra racconta quel mercato. */
function marketTime(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(instant));
}

function eventHref(event: TimelineEvent, mic: string): string | null {
  if (!event.link) return null;
  switch (event.link.target) {
    case 'briefing':
      return `/markets/${mic}/briefings/${event.link.id}`;
    case 'analysis':
      return `/watchlists/analyses/${event.link.instrumentId}/${event.link.id}`;
    case 'autoTrading':
      return '/auto-trading';
  }
}

export function MarketTimeline({ mic, timezoneIana }: { mic: string; timezoneIana: string }) {
  const timelineQuery = useMarketTimeline(mic);

  if (timelineQuery.isPending) {
    return (
      <Card title="Prossime 24 ore">
        <Spinner label="Caricamento della linea del tempo…" />
      </Card>
    );
  }
  if (timelineQuery.isError) {
    return (
      <Card title="Prossime 24 ore">
        <Alert variant="error" title="Impossibile caricare la linea del tempo">
          Il resto della pagina resta consultabile.
        </Alert>
      </Card>
    );
  }

  const timeline = timelineQuery.data;
  const start = new Date(timeline.windowStart).getTime();
  const span = new Date(timeline.windowEnd).getTime() - start;
  const nowLeft = positionOf(timeline.now, start, span);

  // Tacche orarie ogni tre ore: una per ora sarebbe illeggibile su una barra
  // larga quanto una card, e una ogni sei perderebbe il riferimento proprio
  // nelle ore di apertura.
  const ticks: Array<{ at: string; left: number }> = [];
  for (let offset = 0; offset <= span; offset += 3 * 3600_000) {
    const at = new Date(start + offset).toISOString();
    ticks.push({ at, left: (offset / span) * 100 });
  }

  return (
    <Card
      title="Prossime 24 ore"
      subtitle={`Orari locali di ${timeline.timezoneIana} · include le 6 ore precedenti, per vedere che cosa è già stato deciso`}
      action={
        timeline.triggerActive ? (
          <Badge tone="warning">Trigger attivo</Badge>
        ) : (
          <Badge tone="neutral">Trigger spento</Badge>
        )
      }
    >
      <div className="timeline">
        <div
          className="timeline__track"
          role="img"
          aria-label={
            // La barra è un'immagine composta: senza questa frase un lettore di
            // schermo annuncerebbe una sequenza di div vuoti. Il contenuto vero
            // resta comunque leggibile nell'elenco sotto, che non è una
            // duplicazione ma la versione testuale della stessa informazione.
            `Sessioni e inneschi del mercato ${mic} dalle ${marketTime(timeline.windowStart, timezoneIana)} alle ${marketTime(timeline.windowEnd, timezoneIana)} ora locale`
          }
        >
          {timeline.segments.map((segment) => {
            const left = positionOf(segment.start, start, span);
            const right = positionOf(segment.end, start, span);
            return (
              <div
                key={`${segment.sessionDate}-${segment.phase}-${segment.start}`}
                className={`timeline__segment timeline__segment--${segment.phase}`}
                style={{ left: `${left}%`, width: `${Math.max(right - left, 0.4)}%` }}
                title={`${PHASE_LABELS[segment.phase]} · ${marketTime(segment.start, timezoneIana)}–${marketTime(segment.end, timezoneIana)}${segment.earlyClose ? ' · chiusura anticipata' : ''}`}
              />
            );
          })}

          {timeline.events.map((event) => (
            <span
              key={event.id}
              className={`timeline__marker timeline__marker--${event.tone}${event.future ? ' timeline__marker--future' : ''}`}
              style={{ left: `${positionOf(event.at, start, span)}%` }}
              title={`${marketTime(event.at, timezoneIana)} · ${event.title}`}
            />
          ))}

          {/* Il marcatore «adesso» è un elemento e non il bordo sinistro: la
              finestra comincia sei ore fa, e lasciarlo implicito farebbe
              leggere il passato come futuro. */}
          <div className="timeline__now" style={{ left: `${nowLeft}%` }} aria-hidden="true">
            <span className="timeline__now-label">adesso</span>
          </div>
        </div>

        <div className="timeline__axis" aria-hidden="true">
          {ticks.map((tick) => (
            <span key={tick.at} className="timeline__tick" style={{ left: `${tick.left}%` }}>
              {marketTime(tick.at, timezoneIana)}
            </span>
          ))}
        </div>

        <ul className="timeline__legend">
          {(['pre_market', 'open', 'after_hours'] as TimelinePhase[]).map((phase) => (
            <li key={phase}>
              <span className={`timeline__swatch timeline__segment--${phase}`} /> {PHASE_LABELS[phase]}
            </li>
          ))}
        </ul>

        {timeline.events.length === 0 ? (
          <p className="timeline__empty">
            Nessun innesco programmato e nessuna operazione nelle ore precedenti.
            {!timeline.triggerActive && ' Il trigger di questo mercato è spento.'}
          </p>
        ) : (
          <ol className="timeline__events">
            {timeline.events.map((event) => {
              const href = eventHref(event, mic);
              return (
                <li key={event.id} className={event.future ? 'timeline__event timeline__event--future' : 'timeline__event'}>
                  <span className={`timeline__event-icon timeline__event-icon--${event.tone}`}>
                    <Icon name={EVENT_ICONS[event.kind]} />
                  </span>
                  <span className="timeline__event-time">{marketTime(event.at, timezoneIana)}</span>
                  <span className="timeline__event-body">
                    <span className="timeline__event-title">
                      {href ? <Link to={href}>{event.title}</Link> : event.title}
                    </span>
                    {event.detail && <span className="timeline__event-detail">{event.detail}</span>}
                  </span>
                  <Badge tone={TONE_TO_BADGE[event.tone]}>
                    {event.future ? `${EVENT_KIND_LABELS[event.kind]} · in arrivo` : EVENT_KIND_LABELS[event.kind]}
                  </Badge>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Card>
  );
}
